import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useScrollAnimation, fadeInUpVariants, staggerContainerVariants } from '../hooks/useScrollAnimation';
import editors from '../assets/award1.png';
const SocialProofContainer = styled(motion.div)`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 80px 20px 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: visible;
  z-index: 1;
`;

const Title = styled(motion.h2)`
  font-family: 'Black Han Sans', sans-serif;
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 40px;
  text-align: center;
  transform: uppercase;
  text-transform: uppercase;
  color: #FFFFFF;
  position: relative;
  z-index: 10;
  letter-spacing: -0.02em;
  background: linear-gradient(135deg, #FFFFFF 0%, #A0A0A0 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled(motion.p)`
  font-size: 18px;
  color: #86868b;
  margin-bottom: 30px;
  text-align: center;
  max-width: 600px;
`;

const EditorChoiceBadge = styled(motion.div)`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  border-radius: 20px;
  margin-bottom: 30px;
  
  span {
    margin-left: 8px;
    font-weight: 600;
    color: #FFFFFF;
  }
`;

const ReviewsContainer = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 24px;
  width: 100%;
  max-width: 900px;
  z-index: 5;
  position: relative;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    place-items: center;
  }
`;

const ReviewCard = styled(motion.a)`
  width: 280px;
  min-height: 180px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  text-decoration: none;
  color: inherit;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 100;
  transform-origin: center;
  border: 1px solid rgba(255, 255, 255, 0.2);
  
  @media (max-width: 768px) {
    width: 100%;
    max-width: 280px;
  }
`;

const ReviewTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 6px;
  color: #1d1d1f;
`;

const Stars = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px;
  margin-bottom: 6px;
`;

const Star = styled.span`
  color: #FFD700;
  font-size: 14px;
`;

const ReviewDate = styled.span`
  font-size: 12px;
  color: #86868b;
  margin-left: 8px;
`;

const UserName = styled.span`
  font-size: 12px;
  color: #86868b;
  margin-left: 6px;
`;

const ReviewText = styled.p`
  font-size: 14px;
  line-height: 1.4;
  margin-top: 8px;
  color: #1d1d1f;
  flex-grow: 1;
  overflow-y: auto;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FirstCardWrapper = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const EditorChoiceImage = styled.div`
  width: 130px;
  background-color: #FFFFFF;
  border-radius: 12px;
  z-index: 100;
  height: 120px;
  background-image: url(${editors});
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  margin: 0 auto 20px;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
`;

const BackgroundBlob = styled(motion.div)`
  position: absolute;
  width: 600px;
  height: 600px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6A3093, #A044FF);
  filter: blur(120px);
  opacity: 0.25;
  z-index: 0;
`;

const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: radial-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px);
  background-size: 30px 30px;
  z-index: 1;
  opacity: 0.3;
`;

const FloatingCircle = styled(motion.div)`
  position: absolute;
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  border-radius: 50%;
  background: ${props => props.color};
  filter: blur(5px);
  opacity: 0.2;
  z-index: 0;
`;

const AwardContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 60px;
  z-index: 10;
`;

const AppleAward = styled(motion.div)`
  width: 120px;
  height: 120px;
  background-image: url(${editors});
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  margin-bottom: 15px;
  filter: drop-shadow(0 5px 15px rgba(255, 255, 255, 0.15));
`;

const AwardText = styled(motion.p)`
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.8);
  text-align: center;
  max-width: 300px;
  line-height: 1.5;
`;

const ShapeElement = styled(motion.div)`
  position: absolute;
  background: ${props => props.background || 'linear-gradient(135deg, #6A3093, #A044FF)'};
  width: ${props => props.width || '100px'};
  height: ${props => props.height || '100px'};
  border-radius: ${props => props.borderRadius || '50%'};
  filter: blur(${props => props.blur || '15px'});
  opacity: ${props => props.opacity || '0.2'};
  z-index: 0;
`;

const SocialProof = () => {
  const reviews = [
    {
      title: "Best App Ever! ⭐⭐⭐⭐⭐",
      name: "bubaikea",
      date: "04/02/2025",
      stars: 5,
      text: "This app is a total game-changer! Intuitive, fast, and packed with useful features. I use it every day and can't imagine my life without it.",
      hasImage: false
    },
    {
      title: "Perfect 5/5",
      name: "NG713NG",
      date: "31/01/2025",
      stars: 5,
      text: "Great app! Every day I used to rack my brains over what to cook. Thanks to this app, I can now enjoy delicious and original dishes every day!"
    },
    {
      title: "Try that app,it's worth it!!!",
      name: "Yuliaxnaiwm",
      date: "30/01/2025",
      stars: 5,
      text: "I downloaded the app, I'm still trying to figure it out. But overall, I really like it) Especially the cat Viki ❤️"
    }
  ];

  const appStoreUrl = "https://apps.apple.com/us/app/chef-kats-ai/id6739225693";

  return (
    <SocialProofContainer as="div">
      <BackgroundPattern />
      <BackgroundBlob as="div" />
      
      <ShapeElement 
        as="div"
        width="200px"
        height="200px"
        blur="30px"
        background="linear-gradient(135deg, #6A3093, #A044FF)"
        style={{ top: '15%', right: '10%' }}
      />
      
      <ShapeElement 
        as="div"
        width="150px"
        height="150px"
        borderRadius="30% 70% 70% 30% / 30% 30% 70% 70%"
        background="linear-gradient(135deg, #00C9FF, #0070FF)"
        style={{ bottom: '20%', left: '5%' }}
      />
      
      <FloatingCircle 
        as="div"
        size={120} 
        color="rgba(255, 125, 0, 0.5)"
        style={{ top: '30%', right: '20%' }}
      />
      
      <FloatingCircle 
        as="div"
        size={90} 
        color="rgba(255, 200, 255, 0.5)"
        style={{ bottom: '25%', left: '15%' }}
      />
      
      <Title as="h2">
        Customer Reviews
      </Title>
      
      <ReviewsContainer as="div">
        {reviews.map((review, index) => (
          <ReviewCard
            key={index}
            href={appStoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ 
              y: -8, 
              boxShadow: '0 16px 40px rgba(0, 0, 0, 0.3)', 
              scale: 1.05,
              rotateY: 5,
              rotateX: -2
            }}
            whileTap={{ scale: 0.98 }}
          >
            <ReviewTitle>{review.title}</ReviewTitle>
            <Stars>
              {[...Array(5)].map((_, i) => (
                <Star key={i}>{i < review.stars ? "★" : "☆"}</Star>
              ))}
              <ReviewDate>{review.date}</ReviewDate>
              <UserName>• {review.name}</UserName>
            </Stars>
            <ReviewText>{review.text}</ReviewText>
          </ReviewCard>
        ))}
      </ReviewsContainer>
      
      <AwardContainer as="div">
        <AppleAward 
          as="div"
          animate={{ 
            scale: [1, 1.05, 1],
            rotate: [0, -3, 3, -3, 0],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        />
        <AwardText as="p">
           "Most Innovative Cooking App"
        </AwardText>
      </AwardContainer>
    </SocialProofContainer>
  );
};

export default SocialProof; 